import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getFirestore, collection, getDocs, doc, getDoc, query, where, orderBy } from 'firebase/firestore';
import { geohashQueryBounds } from 'geofire-common';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BusinessCard from '../components/BusinessCard';
import MapView from '../components/MapView';
import './MainPage.css';

const db = getFirestore();

const TAG_ICON_MAP = {
  burgers: 'fa-hamburger',
  pizza: 'fa-pizza-slice',
  sushi: 'fa-fish',
  tacos: 'fa-pepper-hot',
  pastas: 'fa-utensils',
  wings: 'fa-drumstick-bite',
  salads: 'fa-leaf',
  sandwiches: 'fa-bread-slice',
  desserts: 'fa-ice-cream',
  coffee: 'fa-coffee',
  seafood: 'fa-fish',
  chicken: 'fa-drumstick-bite',
  salvadoran: 'fa-globe-americas',
  mexican: 'fa-pepper-hot',
  italian: 'fa-pizza-slice',
  asian: 'fa-bowl-rice',
  vietnamese: 'fa-bowl-food',
  chinese: 'fa-bowl-rice',
  japanese: 'fa-fish',
  american: 'fa-flag-usa',
  mediterranean: 'fa-olive',
  peruvian: 'fa-globe-americas',
  venezuelan: 'fa-globe-americas',
  healthy: 'fa-carrot',
  vegetarian: 'fa-leaf',
  vegan: 'fa-seedling',
  'gluten-free': 'fa-ban',
  keto: 'fa-bacon',
  organic: 'fa-leaf',
  breakfast: 'fa-sun',
  brunch: 'fa-mug-hot',
  lunch: 'fa-utensils',
  dinner: 'fa-moon',
  'late-night': 'fa-clock',
  'fast-food': 'fa-burger',
  'casual-dining': 'fa-chair',
  'fine-dining': 'fa-wine-glass',
  bakery: 'fa-bread-slice',
  drinks: 'fa-glass-water',
};

// Haversine distance in miles
const getDistance = (lat1, lng1, lat2, lng2) => {
  const R = 3959; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) *
    Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const MainPage = () => {
  const navigate = useNavigate();
  const { user, userData, guestLocation, hasLocation, needsAddress, loading: authLoading, favorites, addRemoveFavorite, reloadFavorites } = useAuth();
  // Default mode: Pick Up (delivery is being wired up via Urban Piper —
  // until then pickup is the primary flow). Persisted to localStorage so the
  // checkout can default its order type to the mode the customer browsed in
  // (navigation state takes priority when present).
  const [mode, setModeState] = useState(() => localStorage.getItem('ff_mode') || 'delivery'); // 'delivery' | 'pickup' | 'reserve'
  const setMode = (m) => {
    setModeState(m);
    try { localStorage.setItem('ff_mode', m); } catch (_) {}
  };
  const [stores, setStores] = useState([]);
  const [allStores, setAllStores] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [userFodyes, setUserFodyes] = useState({ balance: 0, backingValue: 0 });

  useEffect(() => {
    const init = async () => {
      // Wait for auth/location state to hydrate before deciding redirects
      if (authLoading) return;

      // If needs address, wait for modal (don't search backend yet)
      if (needsAddress) {
        setLoading(false);
        return;
      }

      // Guard: redirect GUESTS with no location back to home (they must pick
      // an address first). Logged-in users fall through to userData → GPS
      // fallback below — bouncing them to '/' creates an infinite redirect
      // loop with HomePage (which sends logged-in users to /explore), seen
      // as a stuck "Detecting your location..." screen after login.
      if (!hasLocation() && !user) {
        navigate('/');
        return;
      }

      // Determine user location: guestLocation (selected address) > userData (default) > browser GPS
      let loc = null;
      if (guestLocation?.lat != null && guestLocation?.lng != null) {
        loc = { lat: guestLocation.lat, lng: guestLocation.lng };
      } else if (userData?.lat != null && userData?.lng != null) {
        loc = { lat: userData.lat, lng: userData.lng };
      }

      // If no saved location, try browser geolocation once
      if (!loc && navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
        } catch (_) {
          // ignore GPS errors silently
        }
      }

      setUserLocation(loc);
      loadStores(loc);
      loadUserFodyes();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userData, guestLocation, needsAddress, authLoading]);

  useEffect(() => {
    const loadTags = async () => {
      try {
        const q = query(collection(db, 'tags'), orderBy('order', 'asc'));
        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTags(list);
      } catch (e) {
        console.warn('Error loading tags:', e);
      }
    };
    loadTags();
  }, []);

  useEffect(() => {
    // Filter by the order types the store offers for the current mode
    // ('reserve' → 'dine_in'), published to publicStores/{storeId}.orderTypes
    // by the admin deploy. Stores published BEFORE that field existed
    // (no/empty orderTypes) are shown in every mode — defensive until the
    // merchant re-deploys with the new field.
    const orderTypeKey =
      mode === 'delivery' ? 'delivery' : mode === 'pickup' ? 'pickup' : mode === 'reserve' ? 'dine_in' : null;

    let filtered = allStores;
    if (orderTypeKey) {
      filtered = filtered.filter((store) => {
        const raw = store.orderTypes;
        if (!Array.isArray(raw) || raw.length === 0) return true; // legacy store, show everywhere
        return raw.includes(orderTypeKey);
      });
    }

    if (selectedTags.length > 0) {
      filtered = filtered.filter((store) =>
        selectedTags.every((tag) => (store.tags || []).includes(tag))
      );
    }
    setStores(filtered);
  }, [allStores, selectedTags, mode]);

  const loadStores = async (loc) => {
    setLoading(true);
    try {
      let results = [];

      if (loc?.lat != null && loc?.lng != null) {
        // Geo-query: fetch only stores within 15 km using geohash bounds
        const radiusInM = 15 * 1000; // 15 km in meters
        const bounds = geohashQueryBounds([loc.lat, loc.lng], radiusInM);
        const queries = bounds.map((b) =>
          query(
            collection(db, 'publicStores'),
            where('published', '==', true),
            where('geohash', '>=', b[0]),
            where('geohash', '<=', b[1])
          )
        );
        const snaps = await Promise.all(queries.map((q) => getDocs(q)));
        const seen = new Set();
        snaps.forEach((snap) => {
          snap.docs.forEach((docItem) => {
            if (seen.has(docItem.id)) return;
            seen.add(docItem.id);
            const data = docItem.data();
            if (data.latitude && data.longitude) {
              const distance = getDistance(loc.lat, loc.lng, data.latitude, data.longitude);
              // getDistance returns miles; 15 km ≈ 9.32 miles
              if (distance <= 9.32) {
                results.push({ id: docItem.id, ...data, distance });
              }
            }
          });
        });
      } else {
        // Fallback: no location — return empty (or could show featured stores)
        results = [];
      }

      // Load review stats & promotions in parallel
      await Promise.all(
        results.map(async (store) => {
          try {
            const statsDoc = await getDoc(doc(db, 'publicStores', store.id, 'reviewStats', 'summary'));
            if (statsDoc.exists()) {
              const s = statsDoc.data();
              store.averageRating = s.averageRating || 0;
              store.totalReviews = s.totalReviews || 0;
            }
            const promosSnap = await getDocs(collection(db, 'publicStores', store.id, 'promotions'));
            const now = new Date();
            store.promotions = promosSnap.docs.map((d) => d.data()).filter((p) => {
              if (p.active === false) return false;
              if (p.startDate && p.startDate.toDate && p.startDate.toDate() > now) return false;
              if (p.endDate && p.endDate.toDate && p.endDate.toDate() < now) return false;
              return true;
            });
          } catch (e) {
            // ignore
          }
        })
      );

      results.sort((a, b) => a.distance - b.distance);
      results = results.slice(0, 20);
      setAllStores(results);
    } catch (error) {
      console.error('Error loading stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserFodyes = async () => {
    if (!user?.uid) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const d = userDoc.data();
        setUserFodyes({
          balance: d.fodyesBalance || 0,
          backingValue: d.fodyesBackingValue || 0,
        });
      }
    } catch (e) {
      console.warn('Error loading user Fodyes:', e);
    }
  };

  const toggleTag = (slug) => {
    setSelectedTags((prev) =>
      prev.includes(slug) ? prev.filter((t) => t !== slug) : [...prev, slug]
    );
  };

  const clearFilters = () => setSelectedTags([]);

  const handleStoreClick = (store) => {
    addRecentlyViewed(store);
    // In Reserve mode a store click opens the table-booking flow instead of
    // the menu page (dine_in = reservable).
    if (mode === 'reserve') {
      navigate(`/reserve/${store.id}`);
      return;
    }
    const slug = (store.locationName || 'store')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 60);
    // Carry the browsing mode into the store page (and from there to
    // checkout) so the order type matches how the customer got here.
    navigate(`/store/${slug}-${store.id}`, { state: { mode } });
  };

  const navigateToStore = (storeOrFav) => {
    addRecentlyViewed(storeOrFav);
    const name = storeOrFav.locationName || storeOrFav.storeName || 'store';
    const id = storeOrFav.id || storeOrFav.storeId;
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').substring(0, 60);
    navigate(`/store/${slug}-${id}`, { state: { mode } });
  };

  const handleToggleFavorite = async (e, store) => {
    e.stopPropagation();
    try {
      await addRemoveFavorite(store.id, store);
      await reloadFavorites();
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  const getFavoriteStoreDistance = (fav) => {
    if (!userLocation || !fav.latitude || !fav.longitude) return null;
    return getDistance(userLocation.lat, userLocation.lng, fav.latitude, fav.longitude);
  };

  // ── Recently Viewed (localStorage, max 10) ──
  const RECENTLY_VIEWED_KEY = 'ff_recently_viewed';

  const getRecentlyViewed = () => {
    try {
      const raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const addRecentlyViewed = (store) => {
    try {
      if (!store?.id) return;
      const existing = getRecentlyViewed();
      // Remove if already exists so it moves to front
      const filtered = existing.filter((s) => s.id !== store.id);
      const entry = {
        id: store.id,
        locationName: store.locationName || store.storeName || 'Store',
        logoUrl: store.logoUrl || null,
        averageRating: store.averageRating || 0,
        reviewCount: store.reviewCount || 0,
        address: store.address || '',
        latitude: store.latitude || null,
        longitude: store.longitude || null,
        category: store.category || '',
        viewedAt: Date.now(),
      };
      const updated = [entry, ...filtered].slice(0, 10);
      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(updated));
    } catch {
      // ignore storage errors
    }
  };

  const [recentlyViewed] = useState(getRecentlyViewed);

  const getRecentStoreDistance = (s) => {
    if (!userLocation || !s.latitude || !s.longitude) return null;
    return getDistance(userLocation.lat, userLocation.lng, s.latitude, s.longitude);
  };

  return (
    <div className="ff-main-page">
      <Header mode={mode} onModeChange={setMode} />

      {/* Mode Toggle Bar */}
      <div className="ff-mode-bar">
        <div className="container">
          <div className="ff-mode-tabs">
            {/* Delivery first — it's the default/primary mode now that Uber
                Direct fulfillment is live end to end. */}
            <button
              className={`ff-mode-tab ${mode === 'delivery' ? 'active' : ''}`}
              onClick={() => setMode('delivery')}
            >
              <i className="fas fa-motorcycle"></i>
              <span>Delivery</span>
            </button>
            <button
              className={`ff-mode-tab ${mode === 'pickup' ? 'active' : ''}`}
              onClick={() => setMode('pickup')}
            >
              <i className="fas fa-store"></i>
              <span>Pick Up</span>
            </button>
            <button
              className={`ff-mode-tab ${mode === 'reserve' ? 'active' : ''}`}
              onClick={() => setMode('reserve')}
            >
              <i className="fas fa-calendar-check"></i>
              <span>Reserve</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tag Filter Bar */}
      {tags.length > 0 && mode !== 'pickup' && (
        <div className="ff-tags-bar">
          <div className="container">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, marginBottom: 10 }}>
              <div
                style={{
                  display: 'grid',
                  gridAutoFlow: 'column',
                  gridTemplateRows: 'repeat(2, auto)',
                  gap: 8,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  flex: 1,
                  paddingBottom: 4,
                }}
              >
                {tags.map((tag) => {
                  const isSelected = selectedTags.includes(tag.slug);
                  const iconClass = TAG_ICON_MAP[tag.slug] || 'fa-tag';
                  return (
                    <button
                      key={tag.id}
                      onClick={() => toggleTag(tag.slug)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '5px 10px',
                        borderRadius: 999,
                        border: '1.5px solid #e0e0e0',
                        background: isSelected ? '#ff5722' : '#fff',
                        color: isSelected ? '#fff' : '#555',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s',
                        height: 32,
                      }}
                      title={tag.label || tag.slug}
                    >
                      <i className={`fas ${iconClass}`} style={{ fontSize: '0.72rem' }}></i>
                      <span>{tag.label || tag.slug}</span>
                    </button>
                  );
                })}
              </div>
              {selectedTags.length > 0 && (
                <button
                  onClick={clearFilters}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ff5722',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    padding: '4px 0',
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="ff-main-content">
        {loading ? (
          <div className="ff-loading-stores">
            <div className="ff-spinner"></div>
            <p>Finding restaurants near you...</p>
          </div>
        ) : (
          <>
            {mode === 'pickup' ? (
              <MapView
                stores={stores}
                userLocation={userLocation}
                favorites={favorites}
                userFodyes={userFodyes}
                tags={tags}
                selectedTags={selectedTags}
                toggleTag={toggleTag}
                clearFilters={clearFilters}
                TAG_ICON_MAP={TAG_ICON_MAP}
              />
            ) : (
              <div className="container">
                {/* Promotions banner for delivery */}
                {mode === 'delivery' && (
                  (() => {
                    const allPromos = [];
                    stores.forEach((s) => {
                      (s.promotions || []).forEach((p) => {
                        allPromos.push({ ...p, storeName: s.locationName, storeId: s.id, storeRating: s.averageRating || 0 });
                      });
                    });
                    allPromos.sort((a, b) => (b.storeRating || 0) - (a.storeRating || 0));
                    if (allPromos.length === 0) return null;
                    return (
                      <div className="ff-promos-section">
                        <h3 className="ff-promos-title"><i className="fas fa-tags text-primary mr-2"></i>Active Promotions</h3>
                        <div className="ff-promos-scroll">
                          {allPromos.slice(0, 10).map((promo, idx) => (
                            <div key={idx} className="ff-promo-badge" onClick={() => handleStoreClick({ id: promo.storeId, locationName: promo.storeName })}>
                              <div className="ff-promo-store">{promo.storeName}</div>
                              <div className="ff-promo-name">{promo.name}</div>
                              <div className="ff-promo-value">
                                {promo.type === 'percentage' ? `${promo.value}% OFF` : `$${promo.value.toFixed(2)} OFF`}
                              </div>
                              {promo.code && <div className="ff-promo-code">Code: {promo.code}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()
                )}
                {/* Your Favorites Section */}
                {favorites.length > 0 && (
                  <div className="ff-favorites-section" style={{ marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#333' }}>
                        <i className="fas fa-heart mr-2 text-danger"></i>Your Favorites
                      </h3>
                      <span style={{ fontSize: '0.8rem', color: '#888' }}>{favorites.length} saved</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
                      {favorites.map((fav) => {
                        const dist = getFavoriteStoreDistance(fav);
                        const distText = dist
                          ? dist < 1
                            ? `${(dist * 5280).toFixed(0)} ft`
                            : `${dist.toFixed(1)} mi`
                          : null;
                        return (
                          <div
                            key={fav.storeId}
                            onClick={() => navigateToStore(fav)}
                            style={{
                              minWidth: 160,
                              maxWidth: 180,
                              background: '#fff',
                              borderRadius: 14,
                              border: '1.5px solid #f0f0f0',
                              padding: 12,
                              cursor: 'pointer',
                              position: 'relative',
                              transition: 'all 0.2s',
                              flexShrink: 0,
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#ff5722'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#f0f0f0'; }}
                          >
                            <button
                              onClick={(e) => handleToggleFavorite(e, { id: fav.storeId, ...fav })}
                              style={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                background: 'rgba(255,255,255,0.9)',
                                border: 'none',
                                borderRadius: '50%',
                                width: 28,
                                height: 28,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: '#ff1744',
                                fontSize: '0.85rem',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                                zIndex: 2,
                              }}
                              title="Remove from favorites"
                            >
                              <i className="fas fa-heart"></i>
                            </button>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                              {fav.logoUrl ? (
                                <img
                                  src={fav.logoUrl}
                                  alt={fav.storeName}
                                  style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover', marginBottom: 8 }}
                                />
                              ) : (
                                <div style={{ width: 56, height: 56, borderRadius: 12, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8, color: '#bbb' }}>
                                  <i className="fas fa-store" style={{ fontSize: '1.2rem' }}></i>
                                </div>
                              )}
                              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                {fav.storeName}
                              </div>
                              {distText && (
                                <div style={{ fontSize: '0.75rem', color: '#ff5722', marginTop: 2, fontWeight: 600 }}>
                                  <i className="fas fa-location-arrow mr-1"></i>{distText}
                                </div>
                              )}
                              {!distText && fav.address && (
                                <div style={{ fontSize: '0.7rem', color: '#999', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                  {fav.address}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Recently Viewed Section */}
                {recentlyViewed.length > 0 && (
                  <div className="ff-recently-viewed-section" style={{ marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#333' }}>
                        <i className="fas fa-history mr-2 text-primary"></i>Recently Viewed
                      </h3>
                      <span style={{ fontSize: '0.8rem', color: '#888' }}>{recentlyViewed.length} viewed</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
                      {recentlyViewed.map((rv) => {
                        const dist = getRecentStoreDistance(rv);
                        const distText = dist
                          ? dist < 1
                            ? `${(dist * 5280).toFixed(0)} ft`
                            : `${dist.toFixed(1)} mi`
                          : null;
                        return (
                          <div
                            key={rv.id}
                            onClick={() => navigateToStore(rv)}
                            style={{
                              minWidth: 160,
                              maxWidth: 180,
                              background: '#fff',
                              borderRadius: 14,
                              border: '1.5px solid #f0f0f0',
                              padding: 12,
                              cursor: 'pointer',
                              position: 'relative',
                              transition: 'all 0.2s',
                              flexShrink: 0,
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#ff5722'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#f0f0f0'; }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                              {rv.logoUrl ? (
                                <img
                                  src={rv.logoUrl}
                                  alt={rv.locationName}
                                  style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover', marginBottom: 8 }}
                                />
                              ) : (
                                <div style={{ width: 56, height: 56, borderRadius: 12, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8, color: '#bbb' }}>
                                  <i className="fas fa-store" style={{ fontSize: '1.2rem' }}></i>
                                </div>
                              )}
                              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                {rv.locationName}
                              </div>
                              {distText && (
                                <div style={{ fontSize: '0.75rem', color: '#ff5722', marginTop: 2, fontWeight: 600 }}>
                                  <i className="fas fa-location-arrow mr-1"></i>{distText}
                                </div>
                              )}
                              {!distText && rv.address && (
                                <div style={{ fontSize: '0.7rem', color: '#999', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                  {rv.address}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="ff-stores-header">
                  <h2>
                    {mode === 'delivery' ? 'Delivery Available' : 'Reservations'}
                  </h2>
                  <span className="ff-stores-count">{stores.length} places found</span>
                </div>
                {stores.length === 0 ? (
                  <div className="ff-empty-state">
                    <i className="fas fa-store-slash fa-3x"></i>
                    <p>No restaurants available in your area yet.</p>
                  </div>
                ) : (
                  <div className="ff-stores-grid">
                    {stores.map((store) => (
                      <BusinessCard
                        key={store.id}
                        store={store}
                        mode={mode}
                        userFodyes={userFodyes}
                        onClick={() => handleStoreClick(store)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default MainPage;
